import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { initialsOf } from '@/lib/utils'

export function Spinner({ size = 20, className }: { size?: number; className?: string }) {
  return (
    <span
      role="status"
      aria-label="Загрузка"
      className={cn('inline-block animate-spin rounded-full border-2 border-current', className)}
      style={{
        width: size,
        height: size,
        borderTopColor: 'transparent',
      }}
    />
  )
}

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('rounded-bubble border border-border bg-surface p-4', className)}
      {...props}
    />
  )
}

type BadgeTone = 'neutral' | 'primary' | 'success' | 'danger' | 'ai'

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-border text-muted',
  primary: 'bg-primary/10 text-primary',
  success: 'bg-emerald-50 text-emerald-700',
  danger: 'bg-danger-soft text-danger',
  ai: 'bg-ai-soft text-ai-strong',
}

export function Badge({
  tone = 'neutral',
  className,
  children,
}: {
  tone?: BadgeTone
  className?: string
  children: ReactNode
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
        BADGE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

export function ProgressBar({
  value,
  max,
  className,
}: {
  value: number
  max: number | null
  className?: string
}) {
  // max === null означает безлимитный тариф — шкалу рисовать не от чего
  const ratio = max && max > 0 ? Math.min(1, value / max) : 0
  const isNearLimit = ratio >= 0.9

  return (
    <div
      className={cn('h-2 w-full overflow-hidden rounded-full bg-border', className)}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      {...(max ? { 'aria-valuemax': max } : {})}
    >
      <div
        className={cn(
          'h-full rounded-full transition-[width] duration-300',
          isNearLimit ? 'bg-danger' : 'bg-primary',
        )}
        style={{ width: `${ratio * 100}%` }}
      />
    </div>
  )
}

export function Avatar({
  name,
  email,
  size = 40,
  className,
}: {
  name?: string | null | undefined
  email?: string | undefined
  size?: number | undefined
  className?: string | undefined
}) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary',
        className,
      )}
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {initialsOf(name, email)}
    </span>
  )
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      {icon ? <div className="mb-4 text-muted-soft">{icon}</div> : null}
      <p className="text-lg font-semibold text-gray-900">{title}</p>
      {description ? <p className="mt-2 max-w-sm whitespace-pre-line text-muted">{description}</p> : null}
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  )
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-card bg-border', className)} />
}

/**
 * Плавающая кнопка действия. На мобильном приподнята над таб-баром,
 * плюс безопасная зона iPhone.
 */
export function Fab({
  onClick,
  label,
  children,
}: {
  onClick: () => void
  label: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        'fixed right-5 z-30 flex h-14 w-14 items-center justify-center rounded-full',
        'bg-primary text-white shadow-lg transition-colors hover:bg-primary-dark',
        'bottom-[calc(5rem+env(safe-area-inset-bottom))] md:bottom-8',
      )}
    >
      {children}
    </button>
  )
}
